import { http, type RequestOptions } from './http';
import { ROUTES } from './config';
import type {
  Booking,
  BookingDetail,
  BookingHistoryPage,
  BookingListFilters,
  BookingListPage,
  BookingSummary,
  Catalog,
  CreateBookingRequest,
  Quote,
  QuoteDetail,
  QuoteRequest,
  QuoteSummary,
} from '../types/domain';

/**
 * BookingService client (catalog + quotes + bookings).
 * Catalog and quotes are public (anonymous booking funnel); the gateway supplies
 * the default tenant. Booking creation requires an Idempotency-Key — the backend
 * rejects the request without one.
 */
export const bookingApi = {
  async getCatalog(): Promise<Catalog> {
    const [areas, packages, items, roomTypes] = await Promise.all([
      http.get<Catalog['areas']>(ROUTES.catalog.serviceAreas, { anonymous: true }),
      http.get<Catalog['packages']>(ROUTES.catalog.packages, { anonymous: true }),
      http.get<Catalog['items']>(ROUTES.catalog.items, { anonymous: true }),
      http.get<Catalog['roomTypes']>(ROUTES.catalog.roomTypes, { anonymous: true }),
    ]);
    return { areas, packages, items, roomTypes };
  },

  /** Dynamic quote. Pass promoCode to apply a campaign discount server-side. */
  createQuote(body: QuoteRequest): Promise<Quote> {
    return http.post(ROUTES.quotes, body, { anonymous: true });
  },

  getQuote(id: string): Promise<QuoteDetail> {
    return http.get(`${ROUTES.quotes}/${id}`);
  },

  /** Admin view — most recent 100 quotes. No update/delete: quotes are immutable price snapshots. */
  listQuotes(): Promise<QuoteSummary[]> {
    return http.get(ROUTES.quotes);
  },

  createBooking(body: CreateBookingRequest, options: RequestOptions & { idempotencyKey?: string } = {}): Promise<Booking> {
    const key = options.idempotencyKey ?? cryptoKey();
    return http.post(ROUTES.bookings, body, { ...options, idempotencyKey: key });
  },

  getBooking(id: string, options?: RequestOptions): Promise<BookingDetail> {
    return http.get(`${ROUTES.bookings}/${id}`, options);
  },

  /** Admin-wide list, newest first, cursor-paginated. Requires the manager role. */
  listBookings(filters: BookingListFilters = {}): Promise<BookingListPage> {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.serviceType) params.set('serviceType', filters.serviceType);
    if (filters.from) params.set('from', filters.from);
    if (filters.to) params.set('to', filters.to);
    if (filters.cursor) params.set('cursor', filters.cursor);
    if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
    const qs = params.toString();
    return http.get(`${ROUTES.bookings}${qs ? `?${qs}` : ''}`);
  },

  /**
   * Cursor-paginated, same as `listBookings` — the type here was wrong until a live test
   * caught it (`bookings.filter is not a function`): the backend's `ByCustomer` action uses
   * the same `ToCursorPageAsync` helper as the admin-wide list, so it was never a bare array.
   */
  getCustomerBookings(customerId: string, params: { cursor?: string; pageSize?: number } = {}, options?: RequestOptions): Promise<BookingListPage> {
    const q = new URLSearchParams();
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.pageSize) q.set('pageSize', String(params.pageSize));
    const qs = q.toString();
    return http.get(`${ROUTES.bookings}/by-customer/${customerId}${qs ? `?${qs}` : ''}`, options);
  },

  cancelBooking(id: string, reason: string, options?: RequestOptions): Promise<void> {
    return http.post(`${ROUTES.bookings}/${id}/cancel`, { reason }, options);
  },

  /** Cursor-paginated audit trail for one booking, newest first. */
  getBookingHistory(id: string, cursor?: string, pageSize = 20): Promise<BookingHistoryPage> {
    const params = new URLSearchParams({ pageSize: String(pageSize) });
    if (cursor) params.set('cursor', cursor);
    return http.get(`${ROUTES.bookings}/${id}/history?${params.toString()}`);
  },
};

function cryptoKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `booking-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
